import useApi from "@/hooks/useApi";
import React, { useEffect, useState } from "react";
import { SecondaryBlueButton, PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { Rule, triggerDefs } from "./types";
import { Expression } from "@/components/Expressions/types";
import { RuleValue } from "./types";

interface VersionSummary {
  id: number;
  trigger: string;
  priority: number;
  changedBy: string;
  changeType: "Created" | "Updated" | "Deleted";
  savedAt: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatSavedAt(savedAt: string): string {
  // savedAt arrives as "2026-02-28 06:05:56 UTC"
  const date = new Date(savedAt.replace(" UTC", "Z"));
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yestStart = new Date(todayStart);
  yestStart.setDate(yestStart.getDate() - 1);
  const versionDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  if (versionDay.getTime() === todayStart.getTime()) return `Today · ${timeStr}`;
  if (versionDay.getTime() === yestStart.getTime()) return `Yesterday · ${timeStr}`;
  return `${date.toLocaleDateString([], { month: "short", day: "numeric" })} · ${timeStr}`;
}

function shortEmail(email: string): string {
  if (!email) return "Unknown";
  return email.split("@")[0];
}

interface VersionDetail extends VersionSummary {
  ruleJson: string;
  valueJson: string;
}

interface Props {
  rule: Rule;
  close(): void;
  onRestore(restored: Partial<Rule>): void;
}

const CHANGE_TYPE_STYLES: Record<string, string> = {
  Created: "bg-green-100 text-green-700",
  Updated: "bg-blue-100 text-blue-700",
  Deleted: "bg-red-100 text-red-700"
};

export default function RuleVersionHistoryModal({ rule, close, onRestore }: Props) {
  const api = useApi();
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await api.admin.get(`RuleVersions/${rule.id}`);
        if (res.ok) {
          const data = await res.json();
          setVersions(Array.isArray(data) ? data : []);
        }
      } catch {
        setError("Could not load version history.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [api.admin, rule.id]);

  async function handleRestore(versionId: number) {
    setRestoring(true);
    setError(null);
    try {
      const res = await api.admin.get(`RuleVersions/${rule.id}/${versionId}`);
      if (!res.ok) {
        setError("Could not load that version.");
        return;
      }
      const detail: VersionDetail = await res.json();
      const expression: Expression = JSON.parse(detail.ruleJson ?? "[]");
      const value: RuleValue = JSON.parse(detail.valueJson ?? "{}");
      onRestore({
        id: rule.id,
        trigger: detail.trigger as Rule["trigger"],
        priority: detail.priority,
        expression,
        value
      });
    } catch {
      setError("Could not restore that version.");
    } finally {
      setRestoring(false);
    }
  }

  const triggerLabel = (trigger: string) =>
    triggerDefs[trigger as keyof typeof triggerDefs] ?? trigger;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-base font-semibold text-slate-800">Version History</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {triggerLabel(rule.trigger)} — last 20 changes
            </p>
          </div>
          <button
            onClick={close}
            className="text-slate-400 hover:text-slate-600 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading && (
            <p className="text-slate-500 text-sm py-6 text-center">Loading history…</p>
          )}
          {!loading && versions.length === 0 && (
            <p className="text-slate-500 text-sm py-6 text-center">
              No version history yet. Versions are saved automatically when you create, update, or delete a rule.
            </p>
          )}
          {!loading && versions.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {versions.map((v, idx) => {
                const isCurrent = idx === 0 && v.changeType !== "Deleted";
                return (
                  <li
                    key={v.id}
                    className={`py-3 px-2 rounded transition-colors ${
                      isCurrent ? "cursor-default" : "cursor-pointer"
                    } ${selected === v.id ? "bg-cornflower-blue-50" : isCurrent ? "" : "hover:bg-slate-50"}`}
                    onClick={() => !isCurrent && setSelected(selected === v.id ? null : v.id)}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          CHANGE_TYPE_STYLES[v.changeType] ?? "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {v.changeType}
                      </span>
                      <span className="text-sm font-semibold text-slate-800">
                        {formatSavedAt(v.savedAt)}
                      </span>
                      {isCurrent && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-slate-500">
                        {triggerLabel(v.trigger)}
                      </span>
                      <span className="text-slate-200 text-xs">·</span>
                      <span className="text-xs text-slate-400">Priority {v.priority}</span>
                      {v.changedBy && (
                        <>
                          <span className="text-slate-200 text-xs">·</span>
                          <span className="text-xs text-slate-400">by {shortEmail(v.changedBy)}</span>
                        </>
                      )}
                    </div>

                    {selected === v.id && !isCurrent && v.changeType !== "Deleted" && (
                      <div className="mt-2 pt-2 border-t border-slate-100">
                        <PrimaryBlueButton
                          label={restoring ? "Restoring…" : "Restore this version"}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestore(v.id);
                          }}
                        />
                        <p className="text-xs text-slate-400 mt-1.5">
                          Restoring opens the editor pre-filled with this version. Nothing is saved until you click Save.
                        </p>
                      </div>
                    )}
                    {selected === v.id && v.changeType === "Deleted" && (
                      <div className="mt-2 pt-2 border-t border-slate-100">
                        <p className="text-xs text-slate-500">
                          This was the state at deletion. Restoring a deleted rule will open it in the editor as a new rule — you can then save it to recreate it.
                        </p>
                        <div className="mt-1.5">
                          <PrimaryBlueButton
                            label={restoring ? "Restoring…" : "Restore deleted version"}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestore(v.id);
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {error && (
            <p className="text-red-600 text-sm mt-2">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-5 py-3 border-t">
          <SecondaryBlueButton label="Close" onClick={close} />
        </div>
      </div>
    </div>
  );
}
